<script setup>
/**
 * OCHAT: el contenedor de Reservas. Solo pestañas y el router-view.
 *
 * Inventario y tarifas es la única pestaña que no ve todo el mundo: es
 * configuración del hotel, así que se muestra solo a los ADMINISTRADORES de la
 * cuenta. Se usa el mismo helper de permisos que la guarda de rutas, así el menú no
 * ofrece algo que al hacer clic redirige.
 *
 * El permiso es 'administrator' y no 'super_admin': las policies del servidor exigen
 * `@account_user.administrator?`, que mira el ROL de la membresía. Un super admin
 * cuya membresía en esta cuenta sea de agente vería la pestaña y comería un 403 al
 * guardar. Ver el comentario largo en bookings.routes.js.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

const { t } = useI18n();
const { accountId, accountScopedRoute } = useAccount();
const currentUser = useMapGetter('getCurrentUser');

const permissions = computed(() =>
  getUserPermissions(currentUser.value, accountId.value)
);
const canConfigure = computed(() =>
  permissions.value.includes('administrator')
);

const tabs = computed(() => {
  const items = [
    {
      key: 'calendar',
      label: t('BOOKINGS.TABS.CALENDAR'),
      to: accountScopedRoute('bookings_calendar'),
    },
    {
      key: 'list',
      label: t('BOOKINGS.TABS.LIST'),
      to: accountScopedRoute('bookings_list'),
    },
    // Faltaba: la ruta y la entrada del sidebar existían desde el principio, pero la
    // barra de pestañas no la ofrecía, así que desde la sección no había forma de
    // llegar a cargar una reserva a mano.
    {
      key: 'new',
      label: t('BOOKINGS.TABS.NEW'),
      to: accountScopedRoute('bookings_new'),
    },
  ];

  if (canConfigure.value) {
    items.push({
      key: 'inventory',
      label: t('BOOKINGS.TABS.INVENTORY'),
      to: accountScopedRoute('bookings_inventory'),
    });
  }

  return items;
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <div class="flex flex-col gap-4 px-6 pt-6">
      <h1 class="text-heading-1 text-n-slate-12">
        {{ $t('BOOKINGS.HEADER') }}
      </h1>
      <nav
        class="flex gap-1 border-b border-n-weak"
        :aria-label="$t('BOOKINGS.HEADER')"
      >
        <router-link
          v-for="tab in tabs"
          :key="tab.key"
          :to="tab.to"
          class="px-3 py-2 -mb-px text-sm font-medium border-b-2 border-transparent text-n-slate-11 hover:text-n-slate-12"
          active-class="!border-n-brand !text-n-slate-12"
        >
          {{ tab.label }}
        </router-link>
      </nav>
    </div>

    <div class="flex-1 min-h-0 overflow-y-auto px-6 py-4">
      <router-view />
    </div>
  </div>
</template>
